import { Dispatch, forwardRef, useEffect, useMemo, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Tooltip from "../../atoms/tooltip";
import { generatorConfiguration } from "./configuration";
import { useGetAssetsFromStrapiMutation } from "../../../pages/eventCreationWorkflowV2/slices/uploadGetApi";
import { useLazyGetAssetsVideoFromStrapiQuery } from "../../../pages/eventCreationWorkflowV2/slices/uploadGetApi";
import { NewQuestionStateSliceActions } from "../../../pages/question-authoring/slice/newQuestionStateSlice";
import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import ErrorListAlerts from "../../../pages/event-creation-workflow/errorListAlert";
// import ErrorListAlerts from "../event-creation-workflow/errorListAlert";
import "./richTextEditor.css";
import {
  type FileLoader,
  type UploadResponse
} from "@ckeditor/ckeditor5-upload";
import { useUploadContentMutation } from "../../../pages/eventCreationWorkflowV2/slices/gcp-upload-api-";
import AudioUploadPlugin from "./audioPlugin";
import VideoUploadPlugin from "./videoPlugin";
// import TextFileReaderPlugin from "./textPlugin";
// import AppLoader from "../../atoms/app-loader/appLoader";
import { RootState } from "@lxp-app/store/store";
import { useSelector, useDispatch } from "react-redux";
import { useUpdateUploadMutation } from "@lxp-app/pages/eventCreationWorkflowV2/slices/uploadApi";
import { useLazyGetCommonConfigQuery } from "@lxp-app/store/slices/api/commonConfigApi";

// eslint-disable-next-line react-refresh/only-export-components

interface RichTextEditorProps {
  value: string;
  setValue: Dispatch<React.SetStateAction<string>>;
  label?: string;
  name?: string;
  error?: boolean;
  placeholder?: string;
  errorMessage?: string;
  optional?: boolean;
  infoicon?: boolean;
  infoMessage?: string;
  register?: (...args: any[]) => void;
  testId?: string;
  onButtonClick?: (value: any) => void;
  buttonLabel?: string;
  buttonIcon?: string;
  generator?: boolean;
  customButton?: string;
  extraPlugins?: Array<typeof Plugin>;
  labelSize?: string;
  setIsCodeBlockActive?: any;
  subjective?: boolean;
  isMultimediaRequired?: boolean;
}

const RichTextEditor = forwardRef(
  (
    {
      value,
      setValue,
      label,
      name,
      register,
      placeholder = "",
      customButton,
      extraPlugins,
      error,
      optional,
      errorMessage,
      infoicon,
      infoMessage,
      testId,
      onButtonClick,
      buttonLabel,
      buttonIcon,
      generator = false,
      labelSize,
      setIsCodeBlockActive,
      isMultimediaRequired,
      subjective = false
    }: RichTextEditorProps,
    editorRef: any
  ) => {
    const isNotProd = ["dev", "development", "staging"].includes(
      import.meta.env.MODE
    );

    const dispatch = useDispatch();
    const isMultimediaUploaded = useSelector(
      (state: RootState) => state.NewQuestionStateSlice.isMultimediaUploaded
    );
    const multimediaLimitMessage = useSelector(
      (state: RootState) => state.NewQuestionStateSlice.multimediaLimitMessage
    );
    const [uploadStrapiFile] = useUpdateUploadMutation();

    const [editorData, setEditorData] = useState<string>(``);
    const [imageUploadSuccessMsg, setImageUploadSuccessMsg] =
      useState<string>(``);
    const [assetId, setAssetId] = useState<any[]>([]);
    const [audioId, setAudioId] = useState<any[]>([]);
    const [videoId, setVideoId] = useState<any[]>([]);
    const [textId, setTextId] = useState<any[]>([]);
    // const [assetRes, setAssetRes] = useState<null | any>(null);

    // const [getAudioAsset, { isLoading: isAudioUploading }] =
    //   useLazyGetAssetsAudioFromStrapiQuery();
    const [getAssetsFromStrapi, { isLoading: isAudioUploading }] =
      useGetAssetsFromStrapiMutation();
    const [getVideoAsset, { isLoading: isVideoUploading }] =
      useLazyGetAssetsVideoFromStrapiQuery();
    const [uploadFile, { isLoading: isFileUploading }] =
      useUploadContentMutation();

    const [getMultimediaLimit] = useLazyGetCommonConfigQuery();

    const checkFileSize = async (file: File) => {
      const resp = await getMultimediaLimit("multimediaSizeLimit").unwrap();

      const sizeLimitInBytes = resp?.data?.value?.size * 1024 * 1024;
      if (file.size > sizeLimitInBytes) {
        return false;
      } else {
        return true;
      }
    };

    const isExtensionAllowed = async (file: File): Promise<boolean> => {
      const fileExtension = file.name.substring(file.name.lastIndexOf(".") + 1);
      const resp = await getMultimediaLimit("multimediaSizeLimit").unwrap();
      const audioAllowed: string[] = resp?.data?.value?.audioExtAllowed;
      const videoAllowed: string[] = resp?.data?.value?.videoExtAllowed;

      const allAllowedExtensions = [...audioAllowed, ...videoAllowed];

      if (!fileExtension) return false;

      return allAllowedExtensions.includes(fileExtension);
    };

    function uploadAdapter(loader: FileLoader) {
      setImageUploadSuccessMsg("");
      return {
        upload: (): Promise<UploadResponse> => {
          // const isLocked = editorRef.current?.config.get(
          //   "isMultimediaUploaded"
          // );
          // if (isLocked) {
          //   return Promise.reject("Multimedia already uploaded.");
          // }
          return new Promise((resolve, reject) => {
            return loader.file.then(async (file: File | null) => {
              if (file) {
                const isValidSize = await checkFileSize(file);

                if (isValidSize) {
                  const data = {
                    ext: file.name.substring(file.name.lastIndexOf(".")),
                    mime: file.type,
                    name: file.name,
                    size: file.size / 1024
                  };
                  loader.uploadTotal = file.size;
                  dispatch(
                    NewQuestionStateSliceActions.setMultimediaLoadingMessage(
                      "File is being uploaded..."
                    )
                  );
                  dispatch(
                    NewQuestionStateSliceActions.setIsMultimediaUploaded(true)
                  );
                  const result: any = await uploadFile(data);

                  const response: any = await fetch(
                    result?.data?.data?.signedUrl,
                    {
                      method: "PUT",
                      headers: { "Content-Type": file.type },
                      body: file
                    }
                  );
                  setAssetId(result?.data?.data?.id);
                  if (response.ok) {
                    setImageUploadSuccessMsg("Image uploaded successfully.");
                    resolve({
                      default: `asset-${result?.data?.data?.id}`
                    });
                    dispatch(
                      NewQuestionStateSliceActions.setMultimediaLimitMessage("")
                    );
                    dispatch(
                      NewQuestionStateSliceActions.setIsMultimediaUploaded(
                        false
                      )
                    );
                  }
                } else {
                  dispatch(
                    NewQuestionStateSliceActions.setMultimediaLimitMessage(
                      `Upload failed: The selected file exceeds the 100MB per file size limit. Please choose a smaller file to continue.`
                    )
                  );
                }
              } else {
                reject(new Error("fail"));
              }
            });
          });
        }
      };
    }

    function uploadPlugin(editor: any) {
      editor.plugins.get("FileRepository").createUploadAdapter = (
        loader: any
      ) => {
        return uploadAdapter(loader);
      };
    }

    const uploadAudio = async (file: File) => {
      setImageUploadSuccessMsg("");
      try {
        if (file) {
          const isValidSize = await checkFileSize(file);
          const isValidExtension = await isExtensionAllowed(file);
          if (isValidSize && isValidExtension) {
            const data = {
              ext: file.name.substring(file.name.lastIndexOf(".")),
              mime: file.type,
              name: file.name,
              size: file.size / 1024
            };

            dispatch(
              NewQuestionStateSliceActions.setMultimediaLoadingMessage(
                "File is being uploaded..."
              )
            );
            dispatch(
              NewQuestionStateSliceActions.setIsMultimediaUploaded(true)
            );
            const result: any = await uploadFile(data);

            await fetch(result?.data?.data?.signedUrl, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file
            });

            const id = result?.data?.data?.id;
            const resp = await getAssetsFromStrapi([id]);

            if (resp) {
              setAudioId(result?.data?.data?.id);
              setImageUploadSuccessMsg("File uploaded successfully.");
              dispatch(
                NewQuestionStateSliceActions.setMultimediaLimitMessage("")
              );
              dispatch(
                NewQuestionStateSliceActions.setIsMultimediaUploaded(false)
              );
            }

            return resp?.data?.data?.[0].url;
          } else {
            const msg = !isValidSize
              ? "Upload failed: The selected file exceeds the 100MB per file size limit. Please choose a smaller file to continue."
              : "Upload failed: The selected file type is not supported. Please choose a different file to continue.";
            dispatch(
              NewQuestionStateSliceActions.setMultimediaLimitMessage(msg)
            );
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(NewQuestionStateSliceActions.setIsMultimediaUploaded(false));
      }
    };

    // const uploadVideo = async (file: File) => {
    //   try {
    //     if (file) {
    //       const isValidSize = await checkFileSize(file);
    //       const isValidExtension = await isExtensionAllowed(file);
    //       if (isValidSize && isValidExtension) {
    //         const data = {
    //           ext: file.name.substring(file.name.lastIndexOf(".")),
    //           mime: file.type,
    //           name: file.name,
    //           size: file.size / 1024
    //         };

    //         dispatch(
    //           NewQuestionStateSliceActions.setMultimediaLimitMessage(
    //             "File is being uploaded..."
    //           )
    //         );

    //         dispatch(
    //           NewQuestionStateSliceActions.setIsMultimediaUploaded(true)
    //         );

    //         const result: any = await uploadFile(data);

    //         await fetch(result?.data?.data?.signedUrl, {
    //           method: "PUT",
    //           headers: { "Content-Type": file.type },
    //           body: file
    //         });

    //         const id = result?.data?.data?.id;
    //         const resp = await getVideoAsset(id);
    //         if (resp) {
    //           dispatch(
    //             NewQuestionStateSliceActions.setMultimediaLimitMessage("")
    //           );
    //           dispatch(
    //             NewQuestionStateSliceActions.setIsMultimediaUploaded(false)
    //           );
    //         }
    //         setVideoId(result?.data?.data?.id);

    //         return result?.data?.data?.id;
    //       } else {
    //         dispatch(
    //           NewQuestionStateSliceActions.setMultimediaLimitMessage(
    //             `${!isValidSize ? "Upload failed: The selected file exceeds the 100MB size limit. Please choose a smaller file to continue." : !isValidExtension ? "File extension is not allowed." : "File Size exceeds the limit and File extension is not allowed."}`
    //           )
    //         );
    //       }
    //     }
    //   } catch (e) {
    //     console.error(e);
    //   } finally {
    //     dispatch(NewQuestionStateSliceActions.setIsMultimediaUploaded(false));
    //   }
    // };

    // const uploadText = async (file: File) => {
    //   console.log("calling");

    //   const data = {
    //     ext: file.name.substring(file.name.lastIndexOf(".")),
    //     mime: file.type,
    //     name: file.name,
    //     size: file.size / 1024
    //   };

    //   const result: any = await uploadFile(data);

    //   await fetch(result?.data?.data?.signedUrl, {
    //     method: "PUT",
    //     headers: { "Content-Type": file.type },
    //     body: file
    //   });

    //   const id = result?.data?.data?.id;
    //   const resp = await getSingleAsset(id);
    //   console.log("resp", resp);
    //   setAssetRes(resp?.data?.data?.[0]?.url);
    //   const assetUrl = resp?.data?.data?.[0]?.url;

    //   const content = await fetch(assetUrl, {
    //     method: "GET"
    //   }).then((res) => {
    //     if (!res.ok) throw new Error("Failed to fetch asset content");
    //     return res.text();
    //   });
    //   setAssetRes(content);
    //   // if (content && !!content.length) {
    //   //   dispatch(NewQuestionStateSliceActions.setIsMultimediaUploaded(true));
    //   // }
    //   console.log(content);
    //   setTextId(result?.data?.data?.id);

    //   return content;
    // };

    useEffect(() => {
      const editor = editorRef?.current;
      if (editor) {
        editor.config.set("isMultimediaUploaded", isMultimediaUploaded);
        const commandsToRefresh = ["audioUpload", "uploadImage"];
        commandsToRefresh.forEach((cmdName) => {
          editor.commands.get(cmdName)?.refresh();
        });
      }

      if (value) {
        let updatedValue = value;

        // Replace images
        updatedValue = updatedValue.replace(
          /<img\s+([^>]*?)src=["']asset-(\d+)["']([^>]*?)>/gi,
          (_, preAttrs, id, postAttrs) => {
            return `<img src="${import.meta.env.VITE_STRAPI_ASSET_URL}/${id}" ${preAttrs} ${postAttrs}>`;
          }
        );

        // Replace audio
        updatedValue = updatedValue.replace(
          /<audio\s+([^>]*?)src=["']asset-(\d+)["']([^>]*?)>/gi,
          (_, preAttrs, id, postAttrs) => {
            return `<audio controls src="${import.meta.env.VITE_STRAPI_ASSET_AUDIO_URL}/${id}" ${preAttrs} ${postAttrs}></audio>`;
          }
        );

        // Replace video
        // updatedValue = updatedValue.replace(
        //   /<video\s+([^>]*?)src=["']asset-(\d+)["']([^>]*?)>/gi,
        //   (_, preAttrs, id, postAttrs) => {
        //     return `<video controls src="${import.meta.env.VITE_STRAPI_ASSET_VIDEO_URL}/${id}" ${preAttrs} ${postAttrs}></video>`;
        //   }
        // );

        // Replace text
        // const escapedContent = updatedValue
        //   .replace(/&/g, "&amp;")
        //   .replace(/</g, "&lt;")
        //   .replace(/>/g, "&gt;");

        // updatedValue = updatedValue.replace(
        //   new RegExp(`asset-${textId}`, "g"),
        //   `<pre>${escapedContent}</pre>`
        // );

        if (updatedValue !== value) {
          setValue(updatedValue);
        }
        // if (editor && isMultimediaUploaded) {
        //   editor.config.set("isMultimediaUploaded", isMultimediaUploaded);
        // }
      }
    }, [isMultimediaUploaded, value, editorRef, dispatch]);

    const handleEditorChange = (
      _event: unknown,
      editor: ClassicEditor
    ): void => {
      const data = editor.getData();

      setValue ? setValue(data) : setEditorData(data);
    };

    const modifiedGeneratorOptions = useMemo(() => {
      const {
        toolbar: { items }
      } = generatorConfiguration(!!isMultimediaRequired);

      return {
        ...generatorConfiguration(!!isMultimediaRequired),
        toolbar: {
          items: customButton ? [customButton, ...items] : items
        }
      };
    }, [customButton]);

    function filteredToolbarItems(items: any[]) {
      const disallowed: Array<string> = ["link", "sourceEditing", "htmlEmbed"];
      return items?.filter((item) => {
        if (disallowed.includes(item)) return false;
        else if (typeof item === "object" && item.label === "Insert")
          return false;
        return true;
      });
    }

    // 1. Fix the baseConfig extraPlugins array
    const baseConfig = {
      placeholder: placeholder,
      extraPlugins: [
        uploadPlugin as any,
        ...(isMultimediaRequired
          ? [AudioUploadPlugin as any, VideoUploadPlugin as any]
          : []),
        ...(extraPlugins ?? [])
      ],
      ...(isMultimediaRequired && {
        audioUploadFn: uploadAudio
      })
    };

    const coreConfig: any = useMemo(() => {
      if (generator) {
        return modifiedGeneratorOptions;
      }
      return generatorConfiguration(!!isMultimediaRequired);
    }, [generator, modifiedGeneratorOptions, isMultimediaRequired]);

    // const coreConfig = useMemo(() => {
    //   if (generator) {
    //     return modifiedGeneratorOptions;
    //   }

    //   return generatorConfiguration(!!isMultimediaRequired);
    // }, [generator, modifiedGeneratorOptions, isMultimediaRequired]);

    // const baseConfig = {
    //   placeholder: placeholder,
    //   extraPlugins: [
    //     uploadPlugin,
    //     // ...(isMultimediaRequired
    //     //   ? [AudioUploadPlugin, VideoUploadPlugin, TextFileReaderPlugin]
    //     //   : []),
    //     ...(extraPlugins ?? [])
    //   ]
    //   // ...(isMultimediaRequired && {
    //   //   audioUploadFn: uploadAudio,
    //   //   videoUploadFn: uploadVideo,
    //   //   textUploadFn: uploadText
    //   // }
    //   // )
    // };

    // const coreConfig = generator
    //   ? (modifiedGeneratorOptions as any)
    //   : editorConfiguration;

    const subjectiveConfig = {
      ...coreConfig,
      ...baseConfig,
      isMultimediaUploaded,
      toolbar: {
        items: filteredToolbarItems(coreConfig?.toolbar?.items)
      }
    };

    const defaultLinkConfig = {
      link: {
        defaultTarget: "_blank",
        decorators: {
          addTargetBlank: {
            mode: "manual",
            label: "Open in a new tab",
            attributes: {
              target: "_blank",
              rel: "noopener noreferrer"
            }
          }
        }
      }
    };
    const defaultNonSubjectiveConfig = {
      ...coreConfig,
      ...baseConfig,
      ...defaultLinkConfig,
      isMultimediaUploaded
    };

    const configMap = {
      true: subjectiveConfig,
      false: defaultNonSubjectiveConfig
    };

    // const CKEditorConfig =
    //   configMap[String(subjective) as keyof typeof configMap];

    const CKEditorConfig = useMemo(() => {
      const config = configMap[String(subjective) as keyof typeof configMap];
      return config as any;
    }, [subjective, configMap]);

    return (
      <>
        <div className="flex flex-col text-left items-start">
          {label && (
            <div className="w-full flex justify-between items-center mb-1">
              <label
                className={`font-medium text-secondary flex
                ${labelSize ? labelSize : "mb-1"}`}
              >
                {label}
                {optional && (
                  <span className="text-sm px-1 font-thin">(Optional)</span>
                )}
                {infoicon && (
                  <Tooltip
                    message={` ${infoMessage}`}
                    position="left-3  w-[18rem] -mt-3 ms-2"
                  >
                    {" "}
                    <InformationCircleIcon className="w-6 h-6 ps-1" />{" "}
                  </Tooltip>
                )}
              </label>
              {buttonLabel && (
                <button
                  type="button"
                  className="px-2 py-1 font-medium text-secondary border border-[#a67affe6] bg-gradient-to-tr from-[#a67aff19] to-[#73c5ff1a] rounded-md flex items-center"
                  onClick={onButtonClick}
                >
                  {buttonIcon && (
                    <img
                      src={buttonIcon}
                      alt="Icon"
                      width="17px"
                      className="mr-2 text-xs"
                    />
                  )}
                  {buttonLabel}
                </button>
              )}
            </div>
          )}
          <div className="no-tailwind w-[100%]" data-testid={testId}>
            <CKEditor
              config={CKEditorConfig}
              editor={ClassicEditor as any}
              data={value ?? editorData}
              onChange={handleEditorChange as any}
              onReady={(editor) => {
                if (editorRef) editorRef.current = editor;

                const editableElement = editor.ui.getEditableElement();

                const validateAllMedia = () => {
                  if (!editableElement) return;

                  const allMedia =
                    editableElement.querySelectorAll("audio, video");
                  const mediaArray = Array.from(allMedia) as HTMLMediaElement[];

                  const hasError = mediaArray.some(
                    (media) => media.error !== null || media.networkState === 3
                  );

                  if (!hasError) {
                    dispatch(
                      NewQuestionStateSliceActions.setMultimediaBrokenMessage(
                        ""
                      )
                    );
                  }
                };

                const observer = new MutationObserver((mutations) => {
                  let shouldRevalidate = false;

                  mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                      if (node instanceof HTMLElement) {
                        const mediaTags =
                          node.tagName === "AUDIO" || node.tagName === "VIDEO"
                            ? [node]
                            : Array.from(node.querySelectorAll("audio, video"));

                        (mediaTags as HTMLMediaElement[]).forEach(
                          (mediaTag) => {
                            mediaTag.preload = "auto";
                            setTimeout(() => {
                              if (
                                mediaTag.networkState === 3 &&
                                !isAudioUploading
                              ) {
                                dispatch(
                                  NewQuestionStateSliceActions.setMultimediaBrokenMessage(
                                    "The embedded media URL is either broken or not playable."
                                  )
                                );
                              }
                            }, 3000);

                            mediaTag.onloadedmetadata = () =>
                              validateAllMedia();

                            // mediaTag.onerror = () => {
                            //   dispatch(
                            //     NewQuestionStateSliceActions.setMultimediaLimitMessage(
                            //       "The embedded media URL is broken or not playable."
                            //     )
                            //   );
                            // };
                          }
                        );
                      }
                    });

                    if (mutation.removedNodes.length > 0) {
                      shouldRevalidate = true;
                    }
                  });

                  if (shouldRevalidate) {
                    validateAllMedia();
                  }
                });

                if (editableElement) {
                  observer.observe(editableElement, {
                    childList: true,
                    subtree: true
                  });
                }

                const imageCommand = editor.commands.get("uploadImage");

                if (imageCommand) {
                  const originalRefresh =
                    imageCommand.refresh.bind(imageCommand);

                  imageCommand.refresh = function () {
                    originalRefresh();

                    const isLocked = editor.config.get("isMultimediaUploaded");
                    if (isLocked) {
                      this.isEnabled = false;
                    }
                  };
                }
                if (editorRef) {
                  editorRef.current = editor;
                }
                const checkCodeBlockActive = () => {
                  const command = editor.commands.get("codeBlock");
                  const isActive = command && command.value;
                  setIsCodeBlockActive(!!isActive);
                };

                if (subjective && !isNotProd) {
                  editor.editing.view.document.on(
                    "clipboardInput",
                    (evt, data) => {
                      evt.stop();
                    }
                  );
                  editor.editing.view.document.on(
                    "cut",
                    (evt: any, data: any) => {
                      evt.stop();
                    }
                  );

                  editor.editing.view.document.on("copy", (evt, data) => {
                    evt.stop();
                  });
                  editor.editing.view.document.on("keydown", (evt, data) => {
                    const isCtrlOrCmd = data.ctrlKey || data.metaKey;
                    const key = data.key.toLowerCase();

                    if (isCtrlOrCmd && (key === "c" || key === "x")) {
                      data.preventDefault();
                      evt.stop();
                    }
                  });

                  editor.editing.view.document.on("paste", (evt: any, data) => {
                    evt.preventDefault();
                    evt.stop();
                  });
                }

                if (setIsCodeBlockActive) {
                  editor.model.document.selection.on(
                    "change:range",
                    checkCodeBlockActive
                  );

                  editor.model.document.on("change:data", checkCodeBlockActive);

                  const codeBlockCommand = editor.commands.get("codeBlock");
                  if (codeBlockCommand) {
                    codeBlockCommand.on("change:value", checkCodeBlockActive);
                  }

                  checkCodeBlockActive();
                }
              }}
              {...(register ? register(name) : {})}
            />
          </div>
          {error && errorMessage && (
            <p
              data-testid="richtextEditorError"
              id="richtextEditorError"
              className="text-md text-red-500 mt-1 mx-1"
            >
              {errorMessage}
            </p>
          )}
        </div>
        {imageUploadSuccessMsg && (
          <ErrorListAlerts
            severity="success"
            label={imageUploadSuccessMsg}
            messageList={[]}
          />
        )}
        {multimediaLimitMessage && (
          <ErrorListAlerts
            severity="error"
            label={multimediaLimitMessage}
            messageList={[]}
          />
        )}
      </>
    );
  }
);

export default RichTextEditor;
